import React, { useState } from 'react';
import { saveToStorage, StorageKeys, clearAllStorage } from '../utils/storage';

const MarketConfirmation = ({ identifiedMarket, onAcceptMarket, onContinueRefining, onResetToStart }) => {
  const [isAccepting, setIsAccepting] = useState(false);

  const handleAcceptMarket = () => {
    setIsAccepting(true);
    saveToStorage(StorageKeys.CONFIRMED_MARKET, identifiedMarket);
    saveToStorage(StorageKeys.CURRENT_STEP, 4);
    
    setTimeout(() => {
      onAcceptMarket(identifiedMarket);
      setIsAccepting(false);
    }, 1000);
  };

  const handleContinueRefining = () => {
    onContinueRefining();
  };

  const handleClearHistory = () => {
    if (window.confirm('Are you sure you want to clear all data and return to the start?')) {
      clearAllStorage();
      onResetToStart();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <button
        onClick={handleClearHistory}
        className="fixed top-4 right-4 px-3 py-1 text-xs font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50 transition-colors duration-200 z-10"
      >
        DEV: Clear History
      </button>
      <div className="max-w-3xl w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              Market Identified!
            </h2>
            <p className="text-lg text-gray-600">
              Based on our conversation, we've identified your core target market.
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
            <h3 className="text-xl font-semibold text-blue-900 mb-3">
              Your Core Target Market:
            </h3>
            <p className="text-lg text-blue-800 font-medium leading-relaxed">
              {identifiedMarket}
            </p>
          </div>

          <div className="space-y-4 mb-8">
            <h4 className="text-lg font-semibold text-gray-900">What happens next?</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-2">1</span>
                  <span className="font-medium text-gray-900">Market Segmentation</span>
                </div>
                <p className="text-sm text-gray-600">
                  We'll break down your market into specific segments using 6 different approaches
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-2">2</span>
                  <span className="font-medium text-gray-900">Interactive Trees</span>
                </div>
                <p className="text-sm text-gray-600">
                  Explore segment trees by customer type, problem, solution delivery, and more
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center mb-2">
                  <span className="bg-blue-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold mr-2">3</span>
                  <span className="font-medium text-gray-900">Strategic Report</span>
                </div>
                <p className="text-sm text-gray-600">
                  Generate a comprehensive report for your selected market segments
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleAcceptMarket}
              disabled={isAccepting}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:transform-none disabled:opacity-70"
            >
              {isAccepting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Processing...
                </div>
              ) : (
                <div className="flex items-center justify-center">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Accept This Market
                </div>
              )}
            </button>

            <button
              onClick={handleContinueRefining}
              disabled={isAccepting}
              className="flex-1 bg-white hover:bg-gray-50 text-gray-700 font-semibold py-4 px-6 rounded-xl border-2 border-gray-300 hover:border-gray-400 transition-all duration-200 disabled:opacity-50"
            >
              <div className="flex items-center justify-center">
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                </svg>
                Continue Refining
              </div>
            </button>
          </div>

          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-yellow-600 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div>
                <p className="text-sm font-medium text-yellow-800">
                  Take your time to review
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  If this market doesn't feel quite right, click "Continue Refining" to have more conversation with our AI assistant. You can always come back to this step.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketConfirmation;